import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Flag, Heart, Server, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { getGallery, likeProject, unlikeProject, reportProject, type GalleryItem } from '@/api/public'
import { ApiError } from '@/api/client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

const PAGE_SIZE = 20

// ── Report dialog ────────────────────────────────────────────────────────

function ReportButton({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => reportProject(slug, reason.trim() || undefined),
    onSuccess: () => {
      toast.success(t('gallery.reportSuccess'))
      setOpen(false)
      setReason('')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('gallery.reportFailed'))
    },
  })

  // 通報も要ログイン(いいねと同様、無意味な匿名通報を防ぐため)。ダイアログを開く時点でガードする。
  const handleOpenChange = (next: boolean) => {
    if (next && !user) {
      toast.error(t('gallery.reportLoginRequired'))
      return
    }
    setOpen(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <Flag className="mr-1.5 h-3.5 w-3.5" />
          {t('gallery.reportButton')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('gallery.reportDialogTitle')}</DialogTitle>
          <DialogDescription>{t('gallery.reportDialogDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="report-reason">{t('gallery.reportReasonLabel')}</Label>
          <Textarea
            id="report-reason"
            rows={3}
            placeholder={t('gallery.reportReasonPlaceholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('common.saving') : t('gallery.reportSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Like button ──────────────────────────────────────────────────────────

function LikeButton({ item }: { item: GalleryItem }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => (item.liked ? unlikeProject(item.slug) : likeProject(item.slug)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gallery'] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 403) {
        toast.error(t('gallery.likeSelfForbidden'))
      } else {
        toast.error(err instanceof Error ? err.message : t('gallery.likeFailed'))
      }
    },
  })

  const handleClick = () => {
    if (!user) {
      toast.error(t('gallery.likeLoginRequired'))
      return
    }
    mutation.mutate()
  }

  return (
    <Button
      variant={item.liked ? 'default' : 'outline'}
      size="sm"
      disabled={mutation.isPending}
      onClick={handleClick}
    >
      <Heart className={cn('mr-1.5 h-3.5 w-3.5', item.liked && 'fill-current')} />
      {item.liked ? t('gallery.likedButton') : t('gallery.likeButton')} ({item.likeCount})
    </Button>
  )
}

// ── Card ─────────────────────────────────────────────────────────────────

function GalleryCard({ item }: { item: GalleryItem }) {
  const { t } = useTranslation()

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{item.name}</CardTitle>
        {item.ownerDisplayName && (
          <CardDescription className="text-xs">
            {t('gallery.by')}: {item.ownerDisplayName}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="py-0 pb-3 flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3">
          {item.description || t('gallery.noDescription')}
        </p>
      </CardContent>
      <CardFooter className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <LikeButton item={item} />
          <ReportButton slug={item.slug} />
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/shared/${item.slug}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            {t('gallery.openProject')}
          </Link>
        </Button>
      </CardFooter>
    </Card>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────

export function GalleryPage() {
  const { t } = useTranslation()
  const [sort, setSort] = useState<'new' | 'popular'>('new')
  const [items, setItems] = useState<GalleryItem[]>([])
  const [offset, setOffset] = useState(0)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['gallery', sort, offset],
    queryFn: () => getGallery({ sort, limit: PAGE_SIZE, offset }),
  })

  // ソート切り替え時はリセットして最初から表示する
  const handleSortChange = (next: 'new' | 'popular') => {
    if (next === sort) return
    setSort(next)
    setOffset(0)
    setItems([])
  }

  // ページが増えるたびに累積表示(素朴な無限スクロールの代わりに「もっと見る」ボタン方式)
  const displayedItems = offset === 0 ? (data?.items ?? []) : [...items, ...(data?.items ?? [])]

  const handleLoadMore = () => {
    setItems(displayedItems)
    setOffset((o) => o + PAGE_SIZE)
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4">
        <div className="mx-auto max-w-5xl flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Server className="h-5 w-5 text-primary" />
            ScenarioMock
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            {t('gallery.backToTop')}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('gallery.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('gallery.subtitle')}</p>
        </div>

        <div className="flex gap-2">
          <Button
            variant={sort === 'new' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleSortChange('new')}
          >
            {t('gallery.sortNew')}
          </Button>
          <Button
            variant={sort === 'popular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleSortChange('popular')}
          >
            {t('gallery.sortPopular')}
          </Button>
        </div>

        {isLoading && offset === 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="h-40 animate-pulse bg-muted" />
            ))}
          </div>
        )}

        {isError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{t('gallery.loadFailed')}</p>
          </div>
        )}

        {!isLoading && displayedItems.length === 0 && !isError && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
            <p className="text-muted-foreground text-sm">{t('gallery.empty')}</p>
          </div>
        )}

        {displayedItems.length > 0 && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {displayedItems.map((item) => (
                <GalleryCard key={item.slug} item={item} />
              ))}
            </div>

            {data?.hasMore && (
              <div className="flex justify-center pt-2">
                <Button variant="outline" onClick={handleLoadMore} disabled={isLoading}>
                  {t('gallery.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
